import axios from 'axios'
import { searchUrl } from 'urls'
import {
  utmDataTypes,
  utmSolrTerms,
  fullSearchPerPage,
  fullSearchTrunkSize,
  fullSearchSubTrunkSize,
} from 'modules/constants'
import { shippingUrl } from "urls";
import {
  getAxiosDefaultConfig,
  sanitizeFilters,
  getEffectiveFilterKeys,
  getDefaultFilterType,
} from 'utils'
import { defaultDashboardConfig } from 'modules/reportConfigs'
import store from '../../../app/store'
import _ from 'lodash'
import {
  isShippingOrderSearchPath,
  resolveMerchantIdForShippingRisk,
  resolveOrderSolrId,
} from '../../Shipping/utils/riskPreviewIds'
import { resolveRiskCapabilities } from 'sharedComponents/canViewRisk'
import { PERMISSIONS } from 'sharedComponents/Router.permissionRoute'
import { runFullSearchExport as runFullSearchExportCore } from './fullSearchExport'

/**
 * Pure read of the current persona's risk capabilities. Lives in this file
 * (rather than `sharedComponents/canViewRisk`) because the search layer is
 * outside React and must hit the store directly.
 *
 * Returns flags only; does NOT short-circuit shipping/insurance flows. The
 * label and insurance fetches always run regardless.
 */
const readRiskCapabilities = () => {
  const root = (store.getState() as any)?.root || {}
  const admin = !!root.admin
  const permissions: string[] = root.permissions || []
  const { canViewMerchantRisk, canViewOrderRisk } = resolveRiskCapabilities(admin, permissions)
  return { canViewMerchantRisk, canViewOrderRisk }
}

export const MAP_REQUIRED_FACETS = ['billing_country_code', 'billing_province']

export const DEFAULT_ORDER_SEARCH_FACETS = [
  'totals', 'financial_status', 'processing_method', 'payment_method', 'gateway', 'credit_card_type', 'bank_ref_id','source_name', 'customer', 'customer_group_name',
  'fulfillment_status', 'billing_country_code', 'billing_county_name', 'billing_city', 'fulfillment_full_address', 'shipping_providers', 'shipping_methods', 'shipping_country_code', 'shipping_province', 'shipping_county_name', 'shipping_city', 'currency', 'merchant_id', 'channel',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'marketing', 'brand_ids', 'brand_names','sku_ids', 'product_sku_ids', 'bin_picking_numbers', 'vendor_ids', 'coupon_codes', 'sales_channel_name', 'sales_channel_type', 'sales_channel_platform', 'fulfillment_channel_type',
  'options', 'custom_fields', 'customer_form_fields', 'shipping_form_fields', 'product_types', 'category_titles', 'catalog_titles_v1', 'order_tags', 'app_id', 'tax_exempt_category', 'product_metafields', 'order_metafields', 'taxed', 'discounted', 'noted', 'guest', 'points_status', 'gift_cards', 'is_first_order', 'customer_custom_attributes',
]

export const searchServer = () => axios.create({
  baseURL: searchUrl,
  headers: {
    ['session-signature']: store.getState().root.sessionSignature,
    path: location.pathname,
    search: location.search,
  },
  withCredentials: true,
})

// Split into trunks if full search
export const runSearch = async (route, dataTypes, params, config, maxLimit, setFullSearchStatus) => {
  const searchStartTime = performance.now()
  let numTrunk = 1, isFullsearch = params.rows === fullSearchPerPage
  if (isFullsearch) {
    params.rows = fullSearchTrunkSize
    numTrunk = Math.ceil(maxLimit / params.rows)
  }
  let output = {}
  for (var trunkIndex = 1; trunkIndex <= numTrunk; trunkIndex ++) {
    try {
      const trunkResponses = (await Promise.all(isFullsearch ? [0,1,2].map(subIndex => (
        searchServer().post(route, {
          params: {
            ...params,
            rows: fullSearchSubTrunkSize,
            start: params.start + fullSearchSubTrunkSize * subIndex
          },
          config
        })
      )) : [
        searchServer().post(route, { params, config })
      ])).map(r => r.data)
      if (isFullsearch && !store.getState().dashboard.fullSearchStatus.state) {
        // Has been cancelled by user
        throw('cancelled')
      }
      // Merge metadata from trunk responses without O(n²) array copies on each round.
      const mergedMeta = trunkResponses.reduce((racc, trunkResponse) => ({ ...racc, ...trunkResponse }), {})
      for (const cur of dataTypes) {
        delete mergedMeta[cur]
      }
      output = { ...output, ...mergedMeta }
      for (const cur of dataTypes) {
        if (!Array.isArray(output[cur])) {
          output[cur] = []
        }
        for (const trunkResponse of trunkResponses) {
          const chunk = trunkResponse[cur] || []
          for (let i = 0; i < chunk.length; i++) {
            output[cur].push(chunk[i])
          }
        }
      }

      params.start += params.rows
      setFullSearchStatus({
        fetched: params.start,
      })
    }
    catch(error) {
      if (error === 'cancelled') {
        throw('cancelled')
      }
      else if (isFullsearch) {
        console.error(`Failed to get full search trunk ${trunkIndex}/${numTrunk}`, error)
        setFullSearchStatus({
          state: 'failed',
          message: 'Please click the Close button to view/download the current fetched records',
        })
        return output
      }
    }
  }

  // Append shipping label purchased and / or shipping insurance for orders

  const pathname = window.location.pathname

  // Shipping Solr grid uses createOrderSearch → runSearch.
  // PayHelm workflow (labels/insurance/claims) is merged on papaya `/search/orders`
  // when `payhelm_workflow_enriched` is set; legacy client-side fetches remain as fallback.
  if (dataTypes[0] === 'orders' && isShippingOrderSearchPath(pathname)) {
    const { orders } = output
    const workflowEnrichedOnServer = !!(output as { payhelm_workflow_enriched?: boolean })
      .payhelm_workflow_enriched
    const orderNumbers = _.map(orders, 'order_number')
    const merchantIdForRisk = resolveMerchantIdForShippingRisk(params, orders || [])

    const { canViewMerchantRisk, canViewOrderRisk } = readRiskCapabilities()

    const attachShippingPageColumns = (order: any) => {
      const fulfs = order.fulfillments
        ? Array.isArray(order.fulfillments)
          ? order.fulfillments
          : [order.fulfillments]
        : []
      const trackingNums = [
        ...new Set(
          [order.tracking_number, ...fulfs.map((f: any) => f?.tracking_number).filter(Boolean)].filter(Boolean)
        ),
      ]
      const carriers = [
        ...new Set(
          [
            ...(Array.isArray(order.shipping_providers) ? order.shipping_providers : []),
            ...fulfs.map((f: any) => f?.tracking_company).filter(Boolean),
          ].filter(Boolean),
        ),
      ]
      const methods = [
        ...new Set(
          [
            ...(Array.isArray(order.shipping_methods) ? order.shipping_methods : []),
            ...fulfs.map((f: any) => f?.service).filter(Boolean),
          ].filter(Boolean),
        ),
      ]
      return {
        ...order,
        display_tracking_numbers: trackingNums.length ? trackingNums.join(', ') : '—',
        display_shipping_carriers: carriers.length ? carriers.join(', ') : '—',
        display_shipping_methods: methods.length ? methods.join(', ') : '—',
      }
    }

    // Normalize order number for consistent comparison (remove dashes, convert to string)
    const normalizeOrderNumber = (num: string | number): string =>
      String(num).replace(/-/g, '').toLowerCase()

    let insuredMap = new Map<string, Record<string, unknown>>()
    let labeledMap = new Map<string, Record<string, unknown>>()

    if (!workflowEnrichedOnServer) {
      const [result, labelResult] = await Promise.all([
        checkInsuredOrders(orderNumbers),
        checkLabeledOrders(orderNumbers),
      ])
      const { labelResult: labelRes } = labelResult
      const { result: insuranceRes } = result
      insuredMap = new Map(
        (insuranceRes || []).map((o: { order_number: string }) => [
          normalizeOrderNumber(o.order_number),
          o,
        ]),
      )
      labeledMap = new Map(
        (labelRes || []).map((o: { order_number: string }) => [
          normalizeOrderNumber(o.order_number),
          o,
        ]),
      )
    }

    const [merchantRiskSummary, orderRiskSignalResult] = await Promise.all([
      canViewMerchantRisk
        ? fetchMerchantPayhelmRiskSummary(merchantIdForRisk)
        : Promise.resolve(null),
      canViewOrderRisk
        ? fetchOrderRiskSignals(orders || [], merchantIdForRisk)
        : Promise.resolve({ map: new Map<string, any>(), failed: false }),
    ])
    const orderRiskSignalMap = orderRiskSignalResult.map
    const orderRiskFetchFailed = orderRiskSignalResult.failed

    output.orders = orders?.map(order => {
      const { order_number } = order
      const normalized = normalizeOrderNumber(order_number)

      const insuredInformation = workflowEnrichedOnServer
        ? order.insured_shipment_information
        : insuredMap.get(normalized)
      const labeledInformation = workflowEnrichedOnServer
        ? order.labeled_shipment_information
        : labeledMap.get(normalized)

      const row: Record<string, unknown> = {
        // `attachShippingPageColumns` returns `{ ...order, display_* }`, so this
        // both spreads the original order and adds the shipping list columns.
        ...attachShippingPageColumns(order),
        ...(insuredInformation ? { insured_shipment_information: insuredInformation } : {}),
        ...(labeledInformation ? { labeled_shipment_information: labeledInformation } : {}),
        // Risk fields are only attached for personas that can see risk.
        // Merchant users get a row with no `payhelm_merchant_risk` /
        // `order_risk_signal` keys at all - no hint that risk exists.
        ...(canViewMerchantRisk
          ? { payhelm_merchant_risk: merchantRiskSummary }
          : {}),
        ...(canViewOrderRisk
          ? {
              order_risk_signal:
                orderRiskSignalMap.get(resolveOrderSolrId(order)) ||
                ({
                  signal_level: null,
                  signal_score: null,
                  confidence: 'low',
                  supporting_factors: [],
                  factors_used: [],
                  factors_missing: [],
                  data_completeness: orderRiskFetchFailed
                    ? 'Risk signal could not be loaded for this order. Retry shortly.'
                    : 'Limited data for this order signal.',
                  based_on_merchant_baseline: true,
                  merchant_context: {
                    merchant_score: merchantRiskSummary?.score ?? null,
                    merchant_level: merchantRiskSummary?.risk_level ?? null,
                  },
                  updated_at: merchantRiskSummary?.updated_at,
                  error: orderRiskFetchFailed ? 'request_failed' : 'limited_data',
                } as any),
            }
          : {}),
      }
      // Strip any Solr-injected risk keys if the session lost risk permissions
      // (defense-in-depth vs. stale Solr payloads).
      if (!canViewMerchantRisk) delete row.payhelm_merchant_risk
      if (!canViewOrderRisk) delete row.order_risk_signal
      if (!canViewMerchantRisk) {
        delete row.risk_band
        delete row.risk_summary
        delete row.merchant_risk_assessment
        delete row.merchant_risk_summary
      }
      if (!canViewOrderRisk) {
        delete row.order_risk_summary
      }
      return row
    })
  }

  // Close the modal as soon as Solr batches finish; Redux/formatting large result sets can block the main thread for a long time.
  // CSV export uses runFullSearchExport instead — it keeps the progress bar up until the file is written.
  if (isFullsearch && typeof setFullSearchStatus === 'function') {
    setFullSearchStatus({ state: null })
  }

  return output
}

/**
 * Full-search trunking for CSV export only.
 * - Forces facets: [] on every sub-request (does not rely on page_action).
 * - Does not write to Redux / Dashboard.newSearch.
 * - Does not clear fullSearchStatus when Solr finishes (caller clears after download).
 * - Continues until a short trunk (does not stop solely on stats.total_orders).
 * - Optional onTrunk: process each trunk and drop it (avoids retaining all rows).
 *   When onTrunk is omitted, rows are accumulated with push and returned.
 */
export const runFullSearchExport = async (
  route,
  dataTypes,
  params,
  config,
  maxLimit,
  setFullSearchStatus,
  onTrunk,
) => {
  return runFullSearchExportCore({
    post: (exportRoute, body) => searchServer().post(exportRoute, body),
    isCancelled: () => !store.getState().dashboard.fullSearchStatus.state,
    route,
    dataTypes,
    params,
    config,
    maxLimit,
    setFullSearchStatus,
    onTrunk,
  })
}

export const createTrendSearch = (
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate }, // date range param
  dateType,
  { trendKeys, trendsGroupBy },
  accountFilter,
  config,
  timestamp,
  setFullSearchStatus,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker
) => {
  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
  }
  if (trendsGroupBy) {
    params.group_by = trendsGroupBy
  }
  if (trendKeys) {
    params.trend_keys = trendKeys
  }
  return runSearch('/trends', ['trends'], params, config, 0, setFullSearchStatus)
}

export const createOrderSearch = (
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate }, // date range param
  dateType,
  { page, perPage, newPage, newPerPage, newSearchOnlyFor, newSortOrder = {} },
  accountFilter,
  isPageAction,
  config,
  maxLimit,
  timestamp,
  setFullSearchStatus,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker,
  extraParams = {},
) => {
  const { facetsOverride, ...restExtraParams } = (extraParams || {}) as {
    facetsOverride?: string[]
    [key: string]: unknown
  }
  // Defense-in-depth: never send Solr `risk_band` (filter or sort) unless the
  // session has `risk:view`. This must match the band-filter UI gate and the
  // backend `requireRiskView` middleware — using RISK_VIEW specifically, not
  // the order-detail / merchant-profile capabilities.
  const root = (store.getState() as any)?.root || {}
  const { canViewRisk } = resolveRiskCapabilities(!!root.admin, root.permissions || [])
  const filtersForSearch =
    !canViewRisk &&
    filters &&
    typeof filters === 'object' &&
    Object.prototype.hasOwnProperty.call(filters, 'risk_band')
      ? (() => {
          const { risk_band: _rb, ...rest } = filters as any
          return rest
        })()
      : filters

  // Not to get some facets when it's a pagination action
  const facets =
    facetsOverride ??
    (newSearchOnlyFor && !isPageAction ? [] : DEFAULT_ORDER_SEARCH_FACETS)

  const sortOrderForSearch =
    !canViewRisk &&
    newSortOrder &&
    (newSortOrder as any).sortBy === 'risk_band'
      ? { ...(newSortOrder as any), sortBy: 'processed_at', sortAsc: false }
      : newSortOrder

  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filtersForSearch,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: facets,
    sort: constructSortQuery(sortOrderForSearch),
    rows: newPerPage || perPage,
    start: ((newPage || page) - 1) * (newPerPage || perPage),
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    page_action: isPageAction || '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
    // Extra, caller-specific Solr params (e.g. Shipping's `shipping_tab`, which
    // the server maps to a cross-field lifecycle `fq`). Spread last but the core
    // keys above are intentionally not overridable by callers.
    ...restExtraParams,
  }
  if (params.rows === fullSearchPerPage) {
    setFullSearchStatus({
      recordName: 'order',
      state: 'in-progress',
      total: maxLimit,
      fetched: 0,
     })
  }
  return runSearch('/orders', ['orders'], params, config, maxLimit, setFullSearchStatus)
}

/**
 * Fetch all matching orders for CSV export without touching Dashboard Redux search results.
 * Reads current dashboard filter/date state from the store so Orders.csvPreAction does not
 * need to call Dashboard.newSearch.
 *
 * @param setFullSearchStatus progress callback
 * @param onTrunk optional per-trunk callback; when provided, rows are not retained
 * @returns { orders } when onTrunk is omitted; otherwise empty orders array
 */
export const exportOrdersForCsv = async (setFullSearchStatus, onTrunk) => {
  const state = store.getState()
  const {
    filters: rawFilters = {},
    filterLogicOperator,
    orderSortOrder,
    stats = {},
  } = state.dashboard || {}
  const {
    accountFilter,
    user = {},
  } = state.root || {}
  const { dateFilters = {}, rangeFilters = {}, keywordFilters = {}, timePicker } = {
    dateFilters: state.dateFilters,
    rangeFilters: state.rangeFilters,
    keywordFilters: state.keywordFilters,
    timePicker: state.timePicker,
  }

  const { keywords: keywordsFilter, ['-keywords']: keywordsExcludeFilter, ...filters } = sanitizeFilters(rawFilters)
  const searchTerms = keywordsFilter?.[0] || ''
  const searchExcludeTerms = keywordsExcludeFilter?.[0] || ''
  const {
    effectiveFilterKeys,
    effectiveOptionFilterKeys,
    effectiveCustomFieldFilterKeys,
    effectiveCustomerFormFieldFilterKeys,
    effectiveShippingFormFieldFilterKeys,
    effectiveCustomerCustomAttributesFilterKeys,
    effectiveOrderMetafieldFilterKeys,
    effectiveProductMetafieldFilterKeys,
  } = getEffectiveFilterKeys(filters)

  const { defaultFilterType: defaultDateType } = getDefaultFilterType(dateFilters.dateTypes || {})
  const activeDateType = Object.keys(dateFilters.dateTypes || {}).find(
    (dt) => dateFilters.dateTypes[dt]?.active
  ) || defaultDateType
  const { start: startDate, end: endDate } = dateFilters.dateTypes?.[activeDateType]?.dateRange || {}
  const dateType = activeDateType
  const maxLimit = stats.total_orders || 0
  const config = { ...defaultDashboardConfig, ...(user.dashboardconfig || {}) }
  const timestamp = (new Date()).getTime()

  const root = state.root || {}
  const { canViewRisk } = resolveRiskCapabilities(!!root.admin, root.permissions || [])
  let filtersForSearch = filters
  if (
    !canViewRisk &&
    filters &&
    typeof filters === 'object' &&
    Object.prototype.hasOwnProperty.call(filters, 'risk_band')
  ) {
    const { risk_band: _rb, ...rest } = filters
    filtersForSearch = rest
  }

  let sortOrderForSearch = orderSortOrder || {}
  if (!canViewRisk && sortOrderForSearch?.sortBy === 'risk_band') {
    sortOrderForSearch = { sortBy: 'processed_at', sortAsc: false }
  }

  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filtersForSearch,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: [],
    sort: constructSortQuery(sortOrderForSearch),
    rows: fullSearchTrunkSize,
    start: 0,
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    page_action: true,
    timestamp,
    date_filters: dateFilters.dateTypes,
    range_filters: rangeFilters.rangeTypes,
    keyword_filters: keywordFilters.keywordTypes,
    timePicker,
  }

  if (typeof setFullSearchStatus === 'function') {
    setFullSearchStatus({
      context: 'CSV export',
      recordName: 'order',
      state: 'in-progress',
      total: maxLimit,
      fetched: 0,
    })
  }

  return runFullSearchExport(
    '/orders',
    ['orders'],
    params,
    config,
    maxLimit,
    setFullSearchStatus,
    onTrunk,
  )
}

// For data types: ['product', 'customer', 'brand', 'sku', 'vendor']
export const createSecondarySearch = (
  dataType,
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate },
  dateType,
  { page, perPage, newPage, newPerPage, newSortOrder = {} },
  accountFilter,
  isPageAction,
  config,
  maxLimit,
  timestamp,
  setFullSearchStatus,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker,
) => {
  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: [`${dataType}s`, dataType === 'customer' ? 'customer_totals' : 'product_totals'],
    rows: newPerPage || perPage,
    start: ((newPage || page) - 1) * (newPerPage || perPage),
    sort: constructSortQuery(newSortOrder),
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    page_action: isPageAction || '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
  }
  if (params.rows === fullSearchPerPage) {
    setFullSearchStatus({
      recordName: dataType,
      state: 'in-progress',
      total: maxLimit,
      fetched: 0,
     })
  }
  return runSearch(`/${dataType}s`, [`${dataType}s`], params, config, maxLimit, setFullSearchStatus)
}

export const createMarketingSearch = (
  dataType,
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate },
  dateType,
  { page = {}, perPage = {}, newPage = {}, newPerPage = {}, newSortOrder = {} },
  accountFilter,
  isPageAction,
  config,
  maxLimit,
  timestamp,
  setFullSearchStatus,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker,
) => {
  const effectiveTypes = dataType ? [dataType] : utmDataTypes
  const params = {
    term: utmSolrTerms[dataType] || '',
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: ['marketing'],
    rows: effectiveTypes.map(k => `${utmSolrTerms[k]}:${newPerPage[k] || perPage[k]}`).join(','),
    start: effectiveTypes.map(k => `${utmSolrTerms[k]}:${((newPage[k] || page[k]) - 1) * (newPerPage[k] || perPage[k])}`).join(','),
    sort: effectiveTypes.map(k => `${utmSolrTerms[k]}:${constructSortQuery(newSortOrder[k])}`).join(','),
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    page_action: isPageAction || '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
  }
  if (params.rows === fullSearchPerPage) {
    setFullSearchStatus({
      recordName: 'marketing',
      state: 'in-progress',
      total: maxLimit,
      fetched: 0,
     })
  }
  return runSearch('/marketing', effectiveTypes, params, config, maxLimit, setFullSearchStatus)
}

export const createGoogleAdsSearch = async () => {
  try {
    const { data } = await axios.get('http://localhost:3000/google/ads', {
      ...getAxiosDefaultConfig()
    })
    return data
  } catch (error) {
    return []
  }
}

const constructSearchTerms = (searchTerms, searchExcludeTerms, filterLogicOperator) => {
  const allTerms = [searchTerms, searchExcludeTerms ? `\\!${searchExcludeTerms}` : ''].filter(v => v)
  return allTerms.length > 1 ? allTerms.map(t => `(${t})`).join(` \\${filterLogicOperator}\\ `) : allTerms.join()
}

const constructSecondaryFilterSearchTerm = (filterKey, val) => {
  const trimmedFilterKey = filterKey.replace(/^-/, '')
  return ((val === '[* TO *]') ? `(/${trimmedFilterKey}:.*/)` : `"${trimmedFilterKey}:${val}"`).replace(/'/g, "\\'")
}

// Exported so dashboard widgets can build the SAME Solr fq the dashboard uses for
// its own searches — widget numbers must respect the active filter sidebar.
export const constructFilterQuery = (
  filters,
  effectiveFilterKeys,
  effectiveOptionFilterKeys,
  effectiveCustomFieldFilterKeys,
  effectiveCustomerFormFieldFilterKeys,
  effectiveShippingFormFieldFilterKeys,
  effectiveCustomerCustomAttributesFilterKeys,
  filterLogicOperator = 'AND',
  effectiveOrderMetafieldFilterKeys = [],
  effectiveProductMetafieldFilterKeys = [],
) => {
  let filterTerms = []
  if (effectiveFilterKeys.length) {
    filterTerms = effectiveFilterKeys.map(filterKey => (
      (`${filterKey}:` + (
        filters[filterKey].length > 1 ? (
          '(' + filters[filterKey].map(val => {
              return (val.startsWith('*') && val.endsWith('*'))
                ? val.replace(/\n/g, '')
                : '"' + val.replace(/'/g, "\\'").replace(/\n/g, '') + '"'
          }).join(' OR ') + ')'
        ) : (filters[filterKey][0].startsWith('*') && filters[filterKey][0].endsWith('*'))
          ? filters[filterKey][0].replace(/\n/g, '')
          : `"${filters[filterKey][0].replace(/'/g, "\\'").replace(/\n/g, '')}"`
      )).replace(/"\[\* TO \*\]"/g, '[* TO *]')
    ))
  }
  if (effectiveOptionFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveOptionFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}options:` + (
          filters.options[filterKey].length > 1 ? (
            '(' + filters.options[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.options[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveCustomFieldFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveCustomFieldFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}custom_fields:` + (
          filters.custom_fields[filterKey].length > 1 ? (
            '(' + filters.custom_fields[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.custom_fields[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveCustomerFormFieldFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveCustomerFormFieldFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}customer_form_fields:` + (
          filters.customer_form_fields[filterKey].length > 1 ? (
            '(' + filters.customer_form_fields[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.customer_form_fields[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveShippingFormFieldFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveShippingFormFieldFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}shipping_form_fields:` + (
          filters.shipping_form_fields[filterKey].length > 1 ? (
            '(' + filters.shipping_form_fields[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.shipping_form_fields[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveCustomerCustomAttributesFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveCustomerCustomAttributesFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}customer_custom_attributes:` + (
          filters.customer_custom_attributes[filterKey].length > 1 ? (
            '(' + filters.customer_custom_attributes[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.customer_custom_attributes[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveOrderMetafieldFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveOrderMetafieldFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}order_metafields:` + (
          filters.order_metafields[filterKey].length > 1 ? (
            '(' + filters.order_metafields[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.order_metafields[filterKey][0])
        )
      )),
    ]
  }
  if (effectiveProductMetafieldFilterKeys.length) {
    filterTerms = [
      ...filterTerms,
      ...effectiveProductMetafieldFilterKeys.map(filterKey => (
        `${filterKey.startsWith('-') ? '-' : ''}product_metafields:` + (
          filters.product_metafields[filterKey].length > 1 ? (
            '(' + filters.product_metafields[filterKey].map(val => constructSecondaryFilterSearchTerm(filterKey, val)).join(' OR ') + ')'
          ) : constructSecondaryFilterSearchTerm(filterKey, filters.product_metafields[filterKey][0])
        )
      )),
    ]
  }
  return filterTerms.join(` \\${filterLogicOperator}\\ `)
}

const constructSortQuery = newSortOrder => {
  if (newSortOrder && newSortOrder.hasOwnProperty('sortBy') && newSortOrder.hasOwnProperty('sortAsc')) {
    return `${newSortOrder.sortBy} ${newSortOrder.sortAsc ? 'asc' : 'desc'}`
  }
  else return ''
}

const checkInsuredOrders = async (orderNumbers) => {
  const { data } = await axios.post(
    `${shippingUrl}/retrieve_shipping_insured_orders`,
    {
      orderNumbers: orderNumbers,
    },
    getAxiosDefaultConfig()
  );

  return data;
};

const checkLabeledOrders = async (orderNumbers) => {
  const { data } = await axios.post(
    `${shippingUrl}/retrieve_shipping_labeled_orders`,
    {
      orderNumbers: orderNumbers,
    },
    getAxiosDefaultConfig()
  );

  return data;
};

const MERCHANT_RISK_ASSESSMENT_PATH = '/merchant_risk_assessment'
const ORDER_RISK_SIGNAL_PATH = '/order_risk_signal'

function sanitizeMerchantRiskMessage(raw: string | undefined): string {
  if (!raw || typeof raw !== 'string') return 'Could not load PayHelm risk assessment.'
  if (/request failed with status code\s*\d+/i.test(raw)) return 'Could not load PayHelm risk assessment.'
  return raw
}

/**
 * Merchant-level PayHelm assessment (PayHelmScoreEngine) — same model as Pipeline Risk Intelligence tab
 * (GET /risk/intelligence/:merchantId). Attached to every Shipping row for the active store filter.
 */
export async function fetchMerchantPayhelmRiskSummary(merchantId: string): Promise<Record<string, any>> {
  if (!merchantId) {
    return {
      error: 'no_merchant',
      message: 'Select a store to see PayHelm risk assessment for that merchant.',
    }
  }
  const url = `${shippingUrl}${MERCHANT_RISK_ASSESSMENT_PATH}`
  try {
    const { data } = await axios.post(
      url,
      { merchantId, period: '90d' },
      getAxiosDefaultConfig()
    )
    return data && typeof data === 'object' ? data : {}
  } catch (e: any) {
    const status = e?.response?.status
    const body = e?.response?.data
    const serverMsg = typeof body?.error === 'string' ? body.error : undefined
    const axiosMsg = typeof e?.message === 'string' ? e.message : ''
    console.warn('[shipping] merchant_risk_assessment failed', { url, status, message: axiosMsg })
    if (status === 404) {
      return {
        error: 'endpoint_not_found',
        message:
          'PayHelm risk assessment is unavailable in this environment. Deploy papaya with POST /shipping/merchant_risk_assessment.',
      }
    }
    if (status === 403) {
      return { error: 'forbidden', message: serverMsg || 'You cannot view risk for that merchant.' }
    }
    return {
      error: 'request_failed',
      message: sanitizeMerchantRiskMessage(serverMsg || axiosMsg),
    }
  }
}

/** Generalized order-level signal influenced by merchant baseline (policy-agnostic). */
async function fetchOrderRiskSignals(
  orders: any[],
  merchantId: string,
): Promise<{ map: Map<string, any>; failed: boolean }> {
  const orderIds = [...new Set((orders || []).map(resolveOrderSolrId).filter(Boolean))]
  if (!merchantId || orderIds.length === 0) return { map: new Map(), failed: false }
  const url = `${shippingUrl}${ORDER_RISK_SIGNAL_PATH}`
  try {
    const { data } = await axios.post(
      url,
      { merchantId, orderIds, period: '90d' },
      getAxiosDefaultConfig()
    )
    const out = new Map<string, any>()
    for (const signal of data?.signals || []) {
      if (signal?.order_id != null) out.set(String(signal.order_id), signal)
    }
    return { map: out, failed: false }
  } catch (e: any) {
    // Distinguish a transient fetch failure from "no signal for this order" so
    // rows are tagged `request_failed` (not the misleading `limited_data`).
    console.warn('[shipping] order_risk_signal failed', {
      url,
      status: e?.response?.status,
      message: e?.message,
    })
    return { map: new Map(), failed: true }
  }
}

// ============================================================================
// ASYNC PROGRESSIVE SEARCH FUNCTIONS
// These functions split up heavy processing using requestAnimationFrame
// to avoid Chrome "[Violation] 'loadend' handler took Xms" warnings
// ============================================================================

/**
 * Delays execution until the next animation frame
 * This allows the browser to handle other tasks (rendering, user input)
 */
const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve))

/**
 * Process data in chunks using requestAnimationFrame to avoid blocking
 * @param items Array of items to process
 * @param processor Function to process each item
 * @param chunkSize Number of items to process per frame
 */
export const processInChunks = async <T, R>(
  items: T[],
  processor: (item: T) => R,
  chunkSize = 100
): Promise<R[]> => {
  const results: R[] = []
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)
    results.push(...chunk.map(processor))
    if (i + chunkSize < items.length) {
      await nextFrame()
    }
  }
  return results
}

/**
 * Async wrapper for order search that processes results progressively
 */
export const createOrderSearchAsync = async (
  ...args: Parameters<typeof createOrderSearch>
): Promise<Awaited<ReturnType<typeof createOrderSearch>>> => {
  const result = await createOrderSearch(...args)
  // Allow browser to breathe between heavy operations
  await nextFrame()
  return result
}

/**
 * Resolve specific Shipping orders by their `order_id` directly from the
 * `/orders` search endpoint, bypassing the grid's tab/date/Refine scope.
 *
 * Used by the Shipping deep-link fallback: when a `/shipping/.../<order_id>` URL
 * is opened cold (refresh / shared link), the referenced order is usually not in
 * the current grid page, so the workflow panel can't resolve it. This fetches
 * just those orders so the panel can render.
 *
 * - `shipping_tab: 'orders'` marks the request as a Shipping search (so the
 *   server enables `order_id` matching + workflow/display enrichment) WITHOUT
 *   adding any lifecycle `fq` that could filter the order back out.
 * - A wide date window avoids excluding orders outside the grid's current range.
 * - `order_id` is an exact (untokenized) Solr field, so each id is fetched as its
 *   own exact-match query and the results are merged.
 */
export const fetchShippingOrdersByOrderIds = async (
  orderIds: Array<string | number | null | undefined>,
  { merchantId, config = {} }: { merchantId?: string; config?: any } = {},
): Promise<any[]> => {
  const ids = [...new Set((orderIds || []).map((v) => String(v ?? '').trim()).filter(Boolean))]
  if (!ids.length) return []

  const fetchOne = async (id: string): Promise<any[]> => {
    const params = {
      q: id,
      facets: [],
      rows: 5,
      start: 0,
      merchant_id: merchantId || '',
      // Wide window so an order outside the grid's active date range still resolves.
      start_date: '2000-01-01',
      end_date: '2999-12-31',
      date_type: 'order_date',
      shipping_tab: 'orders',
    }
    try {
      const { data } = await searchServer().post('/orders', { params, config })
      return Array.isArray(data?.orders) ? data.orders : []
    } catch (e) {
      console.warn('[shipping] fetchShippingOrdersByOrderIds failed', { id, error: (e as any)?.message })
      return []
    }
  }

  const batches = await Promise.all(ids.map(fetchOne))
  const merged: any[] = []
  const seen = new Set<string>()
  for (const batch of batches) {
    for (const order of batch) {
      const key = String(order?.order_id ?? order?.order_number ?? '')
      if (!key || seen.has(key)) continue
      seen.add(key)
      merged.push(order)
    }
  }
  return merged
}

/**
 * Async wrapper for trend search that processes results progressively
 */
export const createTrendSearchAsync = async (
  ...args: Parameters<typeof createTrendSearch>
): Promise<Awaited<ReturnType<typeof createTrendSearch>>> => {
  const result = await createTrendSearch(...args)
  await nextFrame()
  return result
}

/**
 * Async wrapper for secondary search that processes results progressively
 */
export const createSecondarySearchAsync = async (
  ...args: Parameters<typeof createSecondarySearch>
): Promise<Awaited<ReturnType<typeof createSecondarySearch>>> => {
  const result = await createSecondarySearch(...args)
  await nextFrame()
  return result
}

/**
 * Async wrapper for marketing search that processes results progressively
 */
export const createMarketingSearchAsync = async (
  ...args: Parameters<typeof createMarketingSearch>
): Promise<Awaited<ReturnType<typeof createMarketingSearch>>> => {
  const result = await createMarketingSearch(...args)
  await nextFrame()
  return result
}

// Type for section search results
export interface SectionSearchResult {
  section: 'orders' | 'trends' | 'stats' | 'facets' | 'products' | 'customers' | 'marketing'
  data: any
  error?: Error
}

/**
 * Execute a search for a specific section with error handling
 * Returns a standardized result object
 */
export const executeSectionSearch = async (
  section: SectionSearchResult['section'],
  searchFn: () => Promise<any>
): Promise<SectionSearchResult> => {
  try {
    const data = await searchFn()
    await nextFrame()
    return { section, data }
  } catch (error) {
    return { section, data: null, error: error as Error }
  }
}

// ============================================================================
// PROGRESSIVE FACET SEARCH FUNCTIONS
// These functions split the monolithic order search into smaller, independent calls
// ============================================================================

// Facet groups for progressive loading - each group is a separate API call
// NOTE: Keys must match both DEFAULT_ORDER_SEARCH_FACETS and filterConfigs keys
export const FACET_GROUPS = {
  // Core stats - always load first (includes totals)
  stats: ['totals'],
  // Financial facets
  financial: ['financial_status', 'processing_method', 'payment_method', 'gateway', 'credit_card_type', 'bank_ref_id'],
  // Customer facets
  customer: ['customer', 'customer_id', 'customer_group_name', 'guest'],
  // Fulfillment/shipping facets
  fulfillment: ['fulfillment_status', 'shipping_providers', 'shipping_methods', 'shipping_country_code', 'shipping_province', 'shipping_county_name', 'shipping_city'],
  // Billing location facets
  billing: ['billing_country_code', 'billing_province', 'billing_county_name', 'billing_city'],
  // Channel/source facets
  channel: ['source_name', 'merchant_id', 'channel', 'sales_channel_name', 'sales_channel_type', 'sales_channel_platform', 'fulfillment_channel_type', 'app_id'],
  // Marketing/UTM facets
  marketing: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'marketing'],
  // Product facets - include both Solr keys and filter config keys
  product: ['brand_ids', 'brand_names', 'sku_ids', 'skus', 'product_sku_ids', 'product_skus', 'bin_picking_numbers', 'vendor_ids', 'vendors', 'product_ids', 'variant_ids', 'marketplace_product_ids', 'product_types', 'category_titles', 'catalog_titles_v1', 'product_metafields'],
  // Order metadata facets
  orderMeta: ['coupon_codes', 'order_tags', 'order_metafields', 'tax_exempt_category', 'taxed', 'discounted', 'noted', 'points_status', 'gift_cards', 'is_first_order', 'currency', 'gateway_fee', 'backcharges_cost'],
  // Custom/form fields
  customFields: ['options', 'custom_fields', 'customer_form_fields', 'shipping_form_fields', 'customer_custom_attributes'],
  // Fulfillment address
  fulfillmentAddress: ['fulfillment_full_address'],
}

// Map individual facet keys to their group
export const getFacetGroup = (facetKey: string): string | null => {
  for (const [groupName, facets] of Object.entries(FACET_GROUPS)) {
    if (facets.includes(facetKey)) {
      return groupName
    }
  }
  return null
}

// Get all facets for a given group
export const getFacetsForGroup = (groupName: string): string[] => {
  return FACET_GROUPS[groupName] || []
}

/**
 * Create a search that fetches only stats (no facets, no orders list)
 * This is the fastest call and should complete first
 */
export const createStatsOnlySearch = (
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate },
  dateType,
  accountFilter,
  config,
  timestamp,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker
) => {
  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: ['totals'], // Only get totals/stats
    rows: 0, // Don't fetch any order rows
    start: 0,
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
  }
  return searchServer().post('/orders', { params, config }).then(r => {
    return { ...r.data, timestamp }
  })
}

/**
 * Create a search that fetches only specific facets
 * Used for progressive facet loading
 */
export const createFacetSearch = (
  facetKeys: string[],
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate },
  dateType,
  accountFilter,
  config,
  timestamp,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker,
  extraParams: Record<string, any> = {},
) => {
  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: facetKeys,
    rows: 0,
    start: 0,
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
    ...extraParams,
  }
  return searchServer().post('/orders', { params, config }).then(r => {
    return {
      facets: r.data.facets,
      // `totals` populates `stats` (total_orders/total_sales). Returned
      // additively so callers that need lightweight pipeline stats (e.g. the
      // Shipping KPI strip) can read it without a second, heavier search.
      stats: r.data.stats,
      facetKeys,
      timestamp
    }
  })
}

/**
 * Create a search that fetches only orders (with pagination) and minimal facets
 * Used when we need the actual order list
 */
export const createOrdersOnlySearch = (
  filters,
  filterLogicOperator,
  { effectiveFilterKeys, effectiveOptionFilterKeys, effectiveCustomFieldFilterKeys, effectiveCustomerFormFieldFilterKeys, effectiveShippingFormFieldFilterKeys, effectiveCustomerCustomAttributesFilterKeys, effectiveOrderMetafieldFilterKeys, effectiveProductMetafieldFilterKeys },
  searchTerms,
  searchExcludeTerms,
  { startDate, endDate },
  dateType,
  { page, perPage, newPage, newPerPage, newSortOrder = {} },
  accountFilter,
  isPageAction,
  config,
  maxLimit,
  timestamp,
  setFullSearchStatus,
  dateFilters,
  rangeFilters,
  keywordFilters,
  timePicker
) => {
  const params = {
    q: constructSearchTerms(searchTerms, searchExcludeTerms, filterLogicOperator),
    fq: constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      filterLogicOperator,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    ),
    start_date: startDate,
    end_date: endDate,
    date_type: dateType,
    logic_operator: filterLogicOperator,
    facets: ['totals'], // Minimal facets - just totals for stats
    sort: constructSortQuery(newSortOrder),
    rows: newPerPage || perPage,
    start: ((newPage || page) - 1) * (newPerPage || perPage),
    merchant_id: (accountFilter && accountFilter.value) ? accountFilter.value : '',
    page_action: isPageAction || '',
    timestamp,
    date_filters: dateFilters,
    range_filters: rangeFilters,
    keyword_filters: keywordFilters,
    timePicker,
  }
  if (params.rows === fullSearchPerPage) {
    setFullSearchStatus({
      recordName: 'order',
      state: 'in-progress',
      total: maxLimit,
      fetched: 0,
     })
  }
  return runSearch('/orders', ['orders'], params, config, maxLimit, setFullSearchStatus)
}

// Type for progressive search callback
export type FacetProgressCallback = (facetKey: string, facetData: any) => void

/**
 * Execute progressive facet search - fetches facets in parallel groups
 * and calls the callback as each group completes
 *
 * @param visibleFacetKeys Array of facet keys that are visible in the user's config
 * @param baseSearchParams Common search parameters
 * @param onFacetComplete Callback called when each facet group completes
 * @returns Promise that resolves when all facets are loaded
 */
export const executeProgressiveFacetSearch = async (
  visibleFacetKeys: string[],
  baseSearchParams: {
    filters: any,
    filterLogicOperator: string,
    effectiveKeys: {
      effectiveFilterKeys: string[],
      effectiveOptionFilterKeys: string[],
      effectiveCustomFieldFilterKeys: string[],
      effectiveCustomerFormFieldFilterKeys: string[],
      effectiveShippingFormFieldFilterKeys: string[],
      effectiveCustomerCustomAttributesFilterKeys: string[],
      effectiveOrderMetafieldFilterKeys?: string[],
      effectiveProductMetafieldFilterKeys?: string[],
    },
    searchTerms: string,
    searchExcludeTerms: string,
    dateRange: { startDate: string, endDate: string },
    dateType: string,
    accountFilter: any,
    config: any,
    timestamp: number,
    dateFilters: any,
    rangeFilters: any,
    keywordFilters: any,
    timePicker: any,
  },
  onFacetComplete: FacetProgressCallback
): Promise<{ [key: string]: any }> => {
  const {
    filters,
    filterLogicOperator,
    effectiveKeys,
    searchTerms,
    searchExcludeTerms,
    dateRange,
    dateType,
    accountFilter,
    config,
    timestamp,
    dateFilters,
    rangeFilters,
    keywordFilters,
    timePicker,
  } = baseSearchParams

  // Batch facets into groups of 5 for optimal API performance
  const BATCH_SIZE = 5
  const facetBatches: string[][] = []

  for (let i = 0; i < visibleFacetKeys.length; i += BATCH_SIZE) {
    facetBatches.push(visibleFacetKeys.slice(i, i + BATCH_SIZE))
  }

  // Create search promises for each batch - all execute in parallel
  const groupPromises = facetBatches.map(async (facetKeys, batchIndex) => {
    const groupName = `batch_${batchIndex}`
    try {
      const result = await createFacetSearch(
        facetKeys,
        filters,
        filterLogicOperator,
        effectiveKeys,
        searchTerms,
        searchExcludeTerms,
        dateRange,
        dateType,
        accountFilter,
        config,
        timestamp,
        dateFilters,
        rangeFilters,
        keywordFilters,
        timePicker,
        { bypass_facet_prune: true },
      )

      // Allow browser to breathe
      await nextFrame()

      // Call callback for each facet in the group (even if empty, to clear loading state)
      for (const facetKey of facetKeys) {
        const facetData = result.facets?.[facetKey] ?? []
        onFacetComplete(facetKey, facetData)
      }

      return { groupName, facets: result.facets }
    } catch (error) {
      console.error(`Failed to fetch facet group ${groupName}:`, error)
      return { groupName, facets: null, error }
    }
  })

  // Execute all batches in parallel
  const results = await Promise.allSettled(groupPromises)

  // Combine all facets into a single object
  const allFacets: { [key: string]: any } = {}
  for (const result of results) {
    if (result.status === 'fulfilled' && result.value.facets) {
      Object.assign(allFacets, result.value.facets)
    }
  }

  return allFacets
}